import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell
} from 'recharts';

// Color palette for diagnoses
const DIAGNOSIS_COLORS = [
  '#4e79a7', '#f28e2c', '#e15759', '#76b7b2', '#59a14f',
  '#edc949', '#af7aa1', '#ff9da7', '#9c755f', '#bab0ab'
];

/**
 * Process raw form data into chart-ready data
 */
export const processChartData = (rawData) => {
  // Count diagnoses
  const diagnosisCounts = {};
  const diagnosisClients = {};

  rawData.forEach(row => {
    const diagnosis = row.diagnosis;
    const clientId = row.clientId;

    if (!diagnosisCounts[diagnosis]) {
      diagnosisCounts[diagnosis] = 0;
      diagnosisClients[diagnosis] = [];
    }
    diagnosisCounts[diagnosis]++;
    if (!diagnosisClients[diagnosis].includes(clientId)) {
      diagnosisClients[diagnosis].push(clientId);
    }
  });

  // Convert to array and sort by count
  const totalRecords = rawData.length;
  const uniqueClients = [...new Set(rawData.map(r => r.clientId))].length;

  const diagnosisData = Object.entries(diagnosisCounts)
    .map(([diagnosis, count], index) => ({
      diagnosis,
      count,
      percentage: (count / totalRecords * 100),
      clients: diagnosisClients[diagnosis].slice(0, 10).join(', ') +
        (diagnosisClients[diagnosis].length > 10 ? `... and ${diagnosisClients[diagnosis].length - 10} more` : ''),
      clientList: diagnosisClients[diagnosis],
      clientCount: diagnosisClients[diagnosis].length,
      color: DIAGNOSIS_COLORS[index % DIAGNOSIS_COLORS.length]
    }))
    .sort((a, b) => b.count - a.count);

  // Calculate diversity metrics
  const totalDiagnoses = diagnosisData.length;
  const top3 = diagnosisData.slice(0, 3);
  const top3Percentage = top3.reduce((sum, d) => sum + d.percentage, 0);

  // Diversity Index calculation
  let baseDiversityScore = Math.min(totalDiagnoses * 1.5, 10);
  let concentrationAdjustment = 1.0;
  if (top3Percentage > 85) concentrationAdjustment = 0.5;
  else if (top3Percentage > 70) concentrationAdjustment = 0.7;
  else if (top3Percentage > 55) concentrationAdjustment = 0.8;

  const diversityIndex = Math.round(baseDiversityScore * concentrationAdjustment * 10) / 10;

  // Practice type
  let practiceType, isSpecialized;
  if (diversityIndex >= 8.5) {
    practiceType = 'highly diverse';
    isSpecialized = false;
  } else if (diversityIndex >= 6.0) {
    practiceType = 'moderately diverse';
    isSpecialized = false;
  } else if (diversityIndex >= 3.5) {
    practiceType = 'somewhat specialized';
    isSpecialized = true;
  } else {
    practiceType = 'highly specialized';
    isSpecialized = true;
  }

  return {
    diagnosisData,
    totalRecords,
    uniqueClients,
    totalDiagnoses,
    top3,
    top3Percentage,
    diversityIndex,
    practiceType,
    isSpecialized,
    primaryDiagnosis: diagnosisData[0] || null
  };
};

/**
 * Custom tooltip component
 */
const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;

  const data = payload[0].payload;

  return (
    <div className="bg-white border border-gray-300 rounded-lg shadow-lg p-4 max-w-xs">
      <p className="font-bold text-gray-900 mb-2">{data.diagnosis}</p>
      <div className="text-sm space-y-1">
        <p><span className="text-gray-600">Count:</span> <span className="font-medium">{data.count}</span></p>
        <p><span className="text-gray-600">Percentage:</span> <span className="font-medium">{data.percentage.toFixed(1)}%</span></p>
        <p><span className="text-gray-600">Clients:</span> <span className="font-medium text-xs">{data.clients}</span></p>
      </div>
    </div>
  );
};

const CaseloadDiagnosisChart = ({ chartData, showLegend = true, showMetrics = true, chartHeight = 400 }) => {
  if (!chartData || !chartData.diagnosisData || chartData.diagnosisData.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-md p-8 text-center">
        <p className="text-gray-500">No data available to display</p>
      </div>
    );
  }

  const { diagnosisData, uniqueClients, totalDiagnoses, diversityIndex, primaryDiagnosis } = chartData;

  // Calculate dynamic height based on number of diagnoses
  const dynamicHeight = Math.max(chartHeight, diagnosisData.length * 40 + 100);

  return (
    <div className="bg-white rounded-xl shadow-md p-6">
      {/* Chart Title */}
      <h3 className="text-xl font-bold text-gray-900 mb-2">Caseload Breakdown by Diagnosis</h3>
      <p className="text-gray-600 text-sm mb-2">Distribution of diagnoses across your caseload</p>
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg px-4 py-2 mb-4">
        <p className="text-xs text-yellow-800">Clinical Decision Support Notice: This tool provides informational analysis to support clinical decision-making. All findings must be reviewed and interpreted by a licensed healthcare professional.</p>
      </div>

      {/* Key Metrics Row */}
      {showMetrics && (
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-blue-50 rounded-lg p-4 text-center">
            <p className="text-sm text-gray-600 font-medium">Total Clients</p>
            <p className="text-2xl font-bold text-blue-700">{uniqueClients}</p>
          </div>
          <div className="bg-green-50 rounded-lg p-4 text-center">
            <p className="text-sm text-gray-600 font-medium">Diagnosis Types</p>
            <p className="text-2xl font-bold text-green-700">{totalDiagnoses}</p>
          </div>
          <div className="bg-purple-50 rounded-lg p-4 text-center">
            <p className="text-sm text-gray-600 font-medium">Diversity Index</p>
            <p className="text-2xl font-bold text-purple-700">{diversityIndex}/10</p>
          </div>
          <div className="bg-orange-50 rounded-lg p-4 text-center">
            <p className="text-sm text-gray-600 font-medium">Primary Focus</p>
            <p className="text-lg font-bold text-orange-700">{primaryDiagnosis?.percentage.toFixed(1)}%</p>
            <p className="text-xs text-gray-500 truncate">{primaryDiagnosis?.diagnosis}</p>
          </div>
        </div>
      )}

      {/* Chart */}
      <div className="w-full" style={{ height: `${dynamicHeight}px` }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={diagnosisData}
            layout="vertical"
            margin={{ top: 20, right: 80, left: 20, bottom: 20 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
            <XAxis
              type="number"
              tick={{ fontSize: 12, fontWeight: 600 }}
              label={{
                value: 'Number of Clients',
                position: 'bottom',
                style: { textAnchor: 'middle', fontSize: 12, fontWeight: 600 }
              }}
            />
            <YAxis
              type="category"
              dataKey="diagnosis"
              tick={{ fontSize: 12, fontWeight: 600 }}
              width={180}
              interval={0}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="count" radius={[0, 4, 4, 0]}>
              {diagnosisData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Legend */}
      {showLegend && (
        <div className="flex flex-wrap justify-center gap-4 mt-4">
          {diagnosisData.slice(0, 5).map((item, index) => (
            <div key={index} className="flex items-center gap-2">
              <div className="w-4 h-4 rounded" style={{ backgroundColor: item.color }}></div>
              <span className="text-sm font-medium text-gray-700">{item.diagnosis}</span>
            </div>
          ))}
          {diagnosisData.length > 5 && (
            <span className="text-sm text-gray-500">+{diagnosisData.length - 5} more</span>
          )}
        </div>
      )}
    </div>
  );
};

export default CaseloadDiagnosisChart;
